import {memo, useCallback, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {isAllowedVideoFormat} from '~/common/fabric/utils';
import {Video} from '~library/atoms/Video';
import {SparkleIcon} from '~library/svg/SparkleIcon';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {VideoAlphaChannel} from '~library/atoms/VideoAlphaChannel';

export const EditorIcon = memo(
  ({
    icon,
    height = 32,
    width = 32,
    onClick,
    isActive,
    id = null,
    hideAnimatedIndicator = false,
    flipEnvelope = null,
    showEnvelopeFront = null,
    circle = false,
    loading = 'lazy',
    outline,
  }) => {
    const isLargishAndUp = useIsLargishAndUp();
    const borderRadius = circle ? '50%' : '0.25rem';
    const loadingRef = useRef(false);
    const iconRef = useRef();
    const loadedRef = useRef(false);
    const isNone = icon === '' || icon?.id === '';

    const defaultVideoSrc = typeof icon === 'object' ? icon?.defaultSrc : icon;
    const isVideo = isAllowedVideoFormat(defaultVideoSrc);
    const dataQaId = isVideo ? 'icon-unit-video-selector' : 'icon-unit-img-selector';

    const className = cx('editor-icon', {
      'editor-icon--clickable': onClick,
      'editor-icon--hidden': !loadedRef.current && !isNone,
      'editor-icon--outline': outline,
    });

    const onClickHandler = useCallback(() => {
      if (!isActive && onClick) {
        if (flipEnvelope) {
          flipEnvelope(showEnvelopeFront);
        }
        onClick(icon, id);
      }
    }, [isActive, icon, id, onClick, flipEnvelope]);

    const finishedLoading = useCallback(() => {
      loadedRef.current = true;
      if (loadingRef.current) loadingRef.current.style.visibility = 'hidden';
      iconRef.current.classList.remove('editor-icon--hidden');
    }, []);

    const style = {
      height,
      minWidth: width,
      width,
    };

    const videoIcon = useMemo(() => {
      if (typeof icon === 'string') {
        return (
          <Video
            ref={iconRef}
            className={className}
            style={{...style, objectFit: 'fill'}}
            onCanPlay={finishedLoading}
            src={icon}
            inViewAutoPlay
          />
        );
      }

      return (
        <VideoAlphaChannel
          ref={iconRef}
          className={className}
          style={{...style, objectFit: 'fill'}}
          onCanPlay={finishedLoading}
          srcVp9={icon?.iconVp9}
          srcH265={icon?.iconH265}
          inViewAutoPlay
        />
      );
    }, [icon, className, style, finishedLoading]);

    const imageIcon = useMemo(() => {
      if (isNone) {
        return (
          <div ref={iconRef} className={cx('editor-icon--none', className)} style={style}>
            <Typography variant="paragraph4" color="charcoal">
              NONE
            </Typography>
          </div>
        );
      }

      return (
        <img
          loading={loading}
          ref={iconRef}
          className={className}
          src={icon}
          onLoad={finishedLoading}
          alt="Icon"
        />
      );
    }, [icon, className, style]);

    return (
      <div className="editor-icon__wrapper" style={style}>
        <div
          style={{height, width, visibility: loadedRef.current ? 'hidden' : 'visible'}}
          className="editor-icon editor-icon--loader"
          ref={loadingRef}
        >
          <span className="evt-spinner inline" />
        </div>
        <Button
          className="editor-icon__button"
          variant="transparent"
          data-qa-id={dataQaId}
          onClick={onClickHandler}
          rounded={circle}
        >
          {isVideo ? videoIcon : imageIcon}
        </Button>
        {isActive && <div className="editor-icon__active-border" style={{borderRadius}} />}
        {isVideo && !hideAnimatedIndicator && (
          <SparkleIcon
            ratio={isLargishAndUp ? 1 : 0.75}
            className="editor-icon__animated-indicator"
          />
        )}
      </div>
    );
  }
);

EditorIcon.propTypes = {
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      defaultSrc: PropTypes.string,
      id: PropTypes.string,
      iconVp9: PropTypes.string,
      iconH265: PropTypes.string,
    }),
  ]).isRequired,
  height: PropTypes.number,
  width: PropTypes.number,
  onClick: PropTypes.func,
  isActive: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideAnimatedIndicator: PropTypes.bool,
  flipEnvelope: PropTypes.func,
  showEnvelopeFront: PropTypes.bool,
  circle: PropTypes.bool,
};
